import React, { useEffect, useRef, useState } from "react";

const answers = [
  {
    keywords: ["education", "master", "masters", "aub", "university", "bachelor", "gpa", "degree"],
    text: "Jad earned his Bachelor's degree in Computer Science and completed Master 1 at the Lebanese University Faculty of Sciences. He graduated with a 4.0/4.0 GPA and is now pursuing a Master's in Computer Science at AUB, with expected graduation in 2028.",
  },
  {
    keywords: ["experience", "internship", "intern", "creoshift", "trainee", "training"],
    text: "Jad completed a Web and Mobile Development internship at Creoshift Lebanon from October 2024 to July 2025. Before that, he completed a Mobile Development training program there from July to September 2024.",
  },
  {
    keywords: ["available", "availability", "hire", "hiring", "freelance", "full time", "opportunity", "opportunities"],
    text: "To discuss Jad's availability or a potential opportunity, please send him an email at jad.m.mcheik@gmail.com.",
  },
  {
    keywords: ["service", "services", "react native", "website development", "web development", "mobile development", "building websites", "build websites", "build a website", "build an app", "what can you build"],
    text: "Jad builds websites of all kinds and cross-platform mobile applications with React Native. He learns quickly and can adapt to the technologies and requirements a project needs.",
  },
  {
    keywords: ["skill", "skills", "technology", "technologies", "stack", "programming", "frontend", "backend", "mobile"],
    text: "Jad's strongest areas are responsive frontend development with React, Next.js, Tailwind CSS, and GSAP; cross-platform mobile development with React Native; and backend/API work with Node.js, Express, MongoDB, MySQL, and Firebase. He also works with Python, machine learning tools, Git, Docker, and cybersecurity tooling.",
  },
  {
    keywords: ["cyberguard", "nvms", "vulnerability", "security", "nmap", "nuclei", "zap", "ffuf"],
    text: "CyberGuard is a team-built Network Vulnerability Monitoring System. Jad contributed across the full stack: the React and Tailwind interface, the Node.js and Express backend, MongoDB and Mongoose data models, and the security workflow. He also worked with Nmap for device discovery, Nuclei for vulnerability and CVE checks, OWASP ZAP and ffuf for web assessment, Dockerized lab environments such as DVWA and OWASP Juice Shop, JWT authentication, audit logs, role-based controls, email alerts, and data backup workflows.",
  },
  {
    keywords: ["project", "projects", "machine learning", "repository", "model", "models"],
    text: "Jad has delivered multiple web and mobile projects during his internship, including responsive websites and cross-platform applications. His featured portfolio projects are a Machine Learning Models Repository—covering regression, classification, clustering, and association-rule learning—and CyberGuard, a Network Vulnerability Monitoring System.",
  },
  {
    keywords: ["language", "languages", "arabic", "english", "french"],
    text: "Jad is a native Arabic speaker, fluent in English, and has intermediate proficiency in French.",
  },
  {
    keywords: ["remote", "international", "location", "lebanon", "Beirut", "where are you located"],
    text: "Jad is based in Beirut, Lebanon, and is open to remote opportunities both within Lebanon and internationally.",
  },
  {
    keywords: ["award", "awards", "ranking", "ranked", "rank", "academic excellence", "certificate", "certificates", "certification", "certifications", "achievement", "achievements"],
    text: "Jad holds a Udemy certificate of completion for Machine Learning A–Z: AI, Python & R + ChatGPT Prize [2025], a 43-hour course completed in October 2025. He was also ranked 1st out of 34 in his Lebanese University graduating class, earning a general average of 86.61/100 with a Very Good appreciation.",
  },
  {
    keywords: ["contact", "email", "phone", "github", "website", "collaborate", "collaboration", "reach out"],
    text: "You can contact Jad anytime at jad.m.mcheik@gmail.com. You can also find him on LinkedIn at linkedin.com/in/jadmcheik, GitHub at github.com/jadmcheik, visit jadmcheik.com, or call +961 81 953 723.",
  },
  {
    keywords: ["professional summary", "profile", "background", "jad mcheik"],
    text: "Jad is a software developer focused on web, mobile, machine learning, and cybersecurity-oriented projects. He enjoys turning practical requirements into clear, scalable, and data-driven solutions.",
  },
];

function getAnswer(question) {
  const normalizedQuestion = question.toLowerCase();
  const hasKeyword = (keyword) => {
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`\\b${escapedKeyword}\\b`, "i").test(normalizedQuestion);
  };
  const match = answers.find(({ keywords }) =>
    keywords.some(hasKeyword)
  );

  return match
    ? match.text
    : "That question doesn't appear to relate to the portfolio. I can help with Jad's education, experience, skills, projects, languages, or contact details.";
}

export default function PortfolioChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const messageListRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi! I can answer questions about Jad's portfolio. Ask about his education, skills, experience, projects, or contact details.",
    },
  ]);

  const sendMessage = (event) => {
    event.preventDefault();
    const trimmedQuestion = question.trim();
    if (trimmedQuestion) askQuestion(trimmedQuestion);
  };

  const askQuestion = (prompt) => {
    setMessages((currentMessages) => [
      ...currentMessages,
      { role: "user", text: prompt },
      { role: "assistant", text: getAnswer(prompt) },
    ]);
    setQuestion("");
  };

  useEffect(() => {
    if (isOpen && messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [isOpen, messages]);

  return (
    <div className="fixed bottom-20 left-5 z-30">
      {isOpen && (
        <section aria-label="Portfolio assistant" className="absolute bottom-16 left-0 flex h-[28rem] w-[min(22rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-[#d4c2ae] bg-[#fdfaf6]/95 shadow-2xl backdrop-blur-md">
          <div className="bg-[#7c5e3c] px-5 py-4 text-white">
            <h2 className="text-base font-heritage-bold">Portfolio Assistant</h2>
            <p className="mt-1 text-xs text-white/85">Answers only from this portfolio</p>
          </div>
          <div className="border-b border-[#d4c2ae] bg-white/70 px-3 py-2">
            <p className="mb-2 text-xs text-[#4e5d4e]">Try asking:</p>
            <div className="flex flex-wrap gap-1.5">
              {["Experience", "Strongest skills", "CyberGuard", "Contact Jad"].map((prompt) => (
                <button key={prompt} type="button" onClick={() => askQuestion(prompt)} className="rounded-full border border-[#d4c2ae] bg-white px-2.5 py-1 text-xs text-[#7c5e3c] transition hover:bg-[#f1ece6]">
                  {prompt}
                </button>
              ))}
            </div>
          </div>
          <div ref={messageListRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm leading-6 ${message.role === "user" ? "ml-auto bg-[#b88c5a] text-white" : "bg-white text-[#4e5d4e] shadow-sm"}`}>
                {message.text}
              </div>
            ))}
          </div>
          <form onSubmit={sendMessage} className="flex gap-2 border-t border-[#d4c2ae] bg-white/80 p-3">
            <label className="sr-only" htmlFor="portfolio-question">Ask a question</label>
            <input id="portfolio-question" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Ask about Jad..." className="min-w-0 flex-1 rounded-xl border border-[#d4c2ae] bg-white px-3 py-2 text-sm text-[#4e5d4e] outline-none focus:border-[#b88c5a]" />
            <button type="submit" className="rounded-xl bg-[#b88c5a] px-3 py-2 text-sm text-white transition hover:bg-[#7c5e3c]">Send</button>
          </form>
        </section>
      )}
      <button type="button" aria-label={isOpen ? "Close portfolio assistant" : "Open portfolio assistant"} aria-expanded={isOpen} onClick={() => setIsOpen((open) => !open)} className="flex h-12 w-12 items-center justify-center rounded-full bg-[#7c5e3c] text-xl text-white shadow-lg transition hover:bg-[#b88c5a] focus-visible:outline-white">
        {isOpen ? "×" : "◌"}
      </button>
    </div>
  );
}
